import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../CSS/CreatePage.css";
import { v4 as uuidv4 } from "uuid"; // Import UUID for generating random hash codes
import { nbaTeams } from "../assets/NBA_Teams"; // Adjust the import path as needed
import { supabase } from "../supabase";

const CreatePage = () => {
  const [leagueName, setLeagueName] = useState("");
  const [people, setPeople] = useState([
    { name: "", teams: [] },
    { name: "", teams: [] },
  ]);
  const navigate = useNavigate();

  const maxTeamsPerPerson = () => {
    switch (people.length) {
      case 2:
        return 15;
      case 3:
        return 10;
      case 4:
        return 7;
      default:
        return 0;
    }
  };

  const handleAddPerson = () => {
    if (people.length < 4) {
      setPeople([...people, { name: "", teams: [] }]);
    }
  };

  const handleRemovePerson = (index) => {
    if (people.length > 2) {
      const newPeople = people.filter((_, i) => i !== index);
      setPeople(newPeople);
    }
  };

  const handleChange = (index, field, value) => {
    const newPeople = [...people];
    if (field === "name") {
      newPeople[index][field] = value;
    } else if (field === "teams") {
      newPeople[index][field] = value.split(",");
    }
    setPeople(newPeople);
  };

  const handleTeamChange = (personIndex, teamIndex, value) => {
    const newPeople = [...people];
    const teamId = Object.keys(nbaTeams).find((key) => nbaTeams[key] === value);
    newPeople[personIndex].teams[teamIndex] = { id: teamId, name: value };
    setPeople(newPeople);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Form submitted:", people); // Debugging log

    // Validate input
    if (!leagueName) {
      alert("Please enter a league name.");
      return;
    }

    for (const person of people) {
      if (!person.name) {
        alert("Please enter a name for all people.");
        return;
      }
      if (person.teams.length !== maxTeamsPerPerson()) {
        alert(`Please select ${maxTeamsPerPerson()} teams for each person.`);
        return;
      }
    }

    // Generate a random hash code for league_id
    const leagueId = uuidv4();

    // Insert data into Supabase
    try {
      // Insert into Leagues table
      const { data: leagueData, error: leagueError } = await supabase
        .from("leagues")
        .insert([{ league_id: leagueId, league_name: leagueName }]);

      if (leagueError) {
        console.error("Error inserting league data:", leagueError);
        alert("Error inserting league data. Please try again.");
        return;
      }

      // Insert into LeaguePlayers table
      const { data: playerData, error: playerError } = await supabase
        .from("leagueplayers")
        .insert(
          people.map((person) => ({
            league_id: leagueId,
            player_name: person.name,
            teams: person.teams, // Store team objects in LeaguePlayers table
          }))
        );

      if (playerError) {
        console.error("Error inserting player data:", playerError);
        alert("Error inserting player data. Please try again.");
        return;
      }

      console.log("Data inserted successfully:", leagueData, playerData);
      const leagueUrl = `www.nbawintotalsbyaurafluxllc.com/league/${leagueId}`;
      navigator.clipboard
        .writeText(leagueUrl)
        .then(() => {
          alert(
            `League Created successfully! Copy this link to access the league: ${leagueUrl}`
          );
          // Redirect to the LeaguePage with the unique league ID
          navigate(`/league/${leagueId}`);
        })
        .catch((err) => {
          console.error("Failed to copy the URL:", err);
          alert(
            `League Created successfully! Copy this link to access the league: ${leagueUrl}`
          );
          // Redirect to the LeaguePage with the unique league ID
          navigate(`/league/${leagueId}`);
        });
    } catch (error) {
      console.error("Error:", error);
      alert("An unexpected error occurred. Please try again.");
    }
  };

  const getAvailableTeams = (personIndex, teamIndex) => {
    const selectedTeams = people.flatMap((person, pIndex) =>
      person.teams
        .filter(
          (team, tIndex) => !(pIndex === personIndex && tIndex === teamIndex)
        )
        .map((team) => team.name)
    );
    return Object.values(nbaTeams).filter(
      (team) => !selectedTeams.includes(team)
    );
  };

  return (
    <div className="create-page">
      <h1>Create</h1>
      <form className="create-form" onSubmit={handleSubmit}>
        <div className="league-info">
          <label htmlFor="league-name">League Name:</label>
          <input
            type="text"
            id="league-name"
            value={leagueName}
            onChange={(e) => setLeagueName(e.target.value)}
          />
        </div>
        {people.map((person, personIndex) => (
          <div key={personIndex} className="person-container">
            <div className="person-info">
              <label htmlFor={`person-${personIndex}`}>
                Person {personIndex + 1}:
              </label>
              <input
                type="text"
                id={`person-${personIndex}`}
                value={person.name}
                onChange={(e) =>
                  handleChange(personIndex, "name", e.target.value)
                }
              />
              <div className="spacer"></div> {/* Spacer added here */}
              {people.length > 2 && (
                <button
                  type="button"
                  className="remove-button"
                  onClick={() => handleRemovePerson(personIndex)}
                >
                  Remove Person
                </button>
              )}
            </div>
            <div className="teams-container">
              {Array.from({ length: maxTeamsPerPerson() }).map(
                (_, teamIndex) => (
                  <div key={teamIndex} className="team-select">
                    <label htmlFor={`team-${personIndex}-${teamIndex}`}>
                      NBA Team {teamIndex + 1}:
                    </label>
                    <select
                      id={`team-${personIndex}-${teamIndex}`}
                      value={person.teams[teamIndex]?.name || ""}
                      onChange={(e) =>
                        handleTeamChange(personIndex, teamIndex, e.target.value)
                      }
                    >
                      <option value="">Select a team</option>
                      {getAvailableTeams(personIndex, teamIndex).map((team) => (
                        <option key={team} value={team}>
                          {team}
                        </option>
                      ))}
                    </select>
                  </div>
                )
              )}
            </div>
          </div>
        ))}
        {people.length < 4 && (
          <button type="button" onClick={handleAddPerson}>
            Add Person
          </button>
        )}
        <button type="submit">Submit</button>
      </form>
    </div>
  );
};

export default CreatePage;
